import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { MdExpandMore as ChevronIcon, MdFiberManualRecord, MdDashboard } from 'react-icons/md';
import * as MdIcons from "react-icons/md";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  styled,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { getRouteMap } from '../Utils/permissionUtils';


// ---- Menu layout (order / grouping / labels) ----
// Names must match the menu names returned by the backend (see getRouteMap).
export const TOP_GROUPS = [
  { label: 'Main', names: ['Dashboard'] },
  { label: 'Business', names: ['Operations', 'Products', 'Promotions', 'Campaign'] },
  { label: 'People', names: ['UserManagement'] },
  { label: 'Insights', names: ['Reports', 'Logs'] },
  { label: 'System', names: ['Settings', 'HR Management', 'Account'] },
];

export const MENU_ORDER = [
  // top level
  'Dashboard', 'Operations', 'Products', 'Promotions', 'Campaign', 'UserManagement', 'Reports', 'Logs', 'Settings', 'HR Management', 'Account',
  // Operations
  'Orders', 'Online Orders', 'Support Ticket', 'Queries', 'Warehouse',
  // Products
  'ProductManagement', 'SellerProductRequest', 'Categories', 'Category Cluster', 'Brands', 'Variation Groups', 'Inventory', 'Expired',
  // Promotions
  'Coupons', 'Combo Offers', 'Combo Offer', 'BOGO Offers', 'BOGO', 'Vouchers', 'Rewards',
  // Campaign
  'Email', 'SMS', 'Whatsapp', 'Push Notification', 'PushNotification',
  // People
  'Customer', 'Users', 'Roles', 'Permissions', 'Sellers', 'Supplier', 'Seller Payout', 'Supplier Payout',
  // Logs
  'ActivityLog', 'LoginLogs', 'Activity',
  // Settings
  'MenuConfiguration', 'Shipping & Payment Config', 'MobileContent', 'AppPolicy', 'FAQs',
  // Account
  'Profile',
];

// Reports is split into themed groups so 22 entries stay scannable.
export const SUBGROUPS = {
  Reports: [
    { label: 'Sales & Orders', items: ['Sales & Revenue Report', 'Order Report', 'Invoice Report', 'Payment Report', 'Shipping Report', 'Return & Refund Report'] },
    { label: 'Products & Stock', items: ['Product Sales Report', 'Category Report', 'Brand Report', 'Inventory Report', 'Customer Ratings Report'] },
    { label: 'Finance & Partners', items: ['Profit & Loss Report', 'GST Summary Report', 'Seller Sales & Commission Report', 'Seller Settlement Report', 'Supplier Purchase Report', 'Supplier Ledger Report'] },
    { label: 'Customers & Marketing', items: ['Customer Report', 'Campaign Report', 'Marketing Report', 'Support Report', 'Warranty Report'] },
  ],
};

// Friendlier labels for names that are stored in code-style form.
export const DISPLAY_NAMES = {
  UserManagement: 'User Management',
  ProductManagement: 'Product Management',
  SellerProductRequest: 'Seller Product Requests',
  ActivityLog: 'Activity Log',
  LoginLogs: 'Login Logs',
  MenuConfiguration: 'Menu Configuration',
  MobileContent: 'Mobile Content',
  AppPolicy: 'App Policy',
};

// ---- Shared styles (defined once, outside the component, so rows don't remount) ----
const StyledNavLink = styled(NavLink)(({ theme }) => ({
  display: 'block',
  width: '100%',
  textDecoration: 'none',
  color: theme.palette.text.primary,
  '&.active .MuiListItemButton-root': {
    backgroundColor: 'rgb(255 209 2)',
    color: 'black',
    fontWeight: 700,
  },
  '&.active .MuiListItemText-primary': { fontWeight: 700 },
}));

// 72px, the same as the top bar, so the two line up across the page
const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 72,
  boxSizing: 'border-box',
  flexShrink: 0,
  padding: '0 20px',
  borderBottom: '1px solid #e5e7eb',
});

const parentButtonSx = {
  borderRadius: '10px',
  mx: 1,
  my: 0.25,
  px: 1.5,
  py: 1,
  color: '#1f2937',
  '&:hover': { backgroundColor: 'rgba(255,209,2,0.35)' },
};
const activeParentSx = { backgroundColor: 'rgba(255,209,2,0.22)' };
const childButtonSx = {
  borderRadius: '8px',
  my: 0.25,
  pl: 1.25,
  pr: 1,
  py: 0.7,
  color: '#374151',
  '&:hover': { backgroundColor: 'rgba(255,209,2,0.35)' },
};
const captionSx = {
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#94a3b8',
  px: 1.25,
  pt: 1,
  pb: 0.25,
};

const Sidebar = ({ isOpen, onClose }) => {
  const [openKey, setOpenKey] = useState(null);
  const location = useLocation();
  const [hrModalOpen, setHrModalOpen] = useState(false);
  const userType = (sessionStorage.getItem("userType") || "").toLowerCase();


  // Dynamic menu items from login response (sessionStorage)
  const [menuItems, setMenuItems] = useState([]);

  // Icon and route mapping for menu names
  // One icon per menu: no two menus or submenus share an icon.
  const iconMap = {
    Dashboard: <MdIcons.MdSpaceDashboard size={20} />,
    Operations: <MdIcons.MdSettingsSuggest size={20} />,
    Products: <MdIcons.MdShoppingBasket size={20} />,
    Promotions: <MdIcons.MdLocalOffer size={20} />,
    Campaign: <MdIcons.MdCampaign size={20} />,
    UserManagement: <MdIcons.MdManageAccounts size={20} />,
    Reports: <MdIcons.MdAssessment size={20} />,
    Logs: <MdIcons.MdEventNote size={20} />,
    Settings: <MdIcons.MdSettings size={20} />,
    "HR Management": <MdIcons.MdBadge size={20} />,
    Account: <MdIcons.MdAccountCircle size={20} />,
    Orders: <MdIcons.MdShoppingCart size={18} />,
    "Online Orders": <MdIcons.MdShoppingBag size={18} />,
    "Support Ticket": <MdIcons.MdSupportAgent size={18} />,
    Warehouse: <MdIcons.MdWarehouse size={18} />,
    ProductManagement: <MdIcons.MdInventory2 size={18} />,
    SellerProductRequest: <MdIcons.MdPlaylistAddCheck size={18} />,
    Categories: <MdIcons.MdCategory size={18} />,
    "Category Cluster": <MdIcons.MdAccountTree size={18} />,
    Brands: <MdIcons.MdBrandingWatermark size={18} />,
    "Variation Groups": <MdIcons.MdTune size={18} />,
    Inventory: <MdIcons.MdAllInbox size={18} />,
    Expired: <MdIcons.MdEventBusy size={18} />,
    Coupons: <MdIcons.MdLocalActivity size={18} />,
    "Combo Offers": <MdIcons.MdCardGiftcard size={18} />,
    "BOGO Offers": <MdIcons.MdLoyalty size={18} />,
    Vouchers: <MdIcons.MdConfirmationNumber size={18} />,
    Rewards: <MdIcons.MdRedeem size={18} />,
    Email: <MdIcons.MdEmail size={18} />,
    SMS: <MdIcons.MdSms size={18} />,
    Whatsapp: <MdIcons.MdChat size={18} />,
    "Push Notification": <MdIcons.MdNotificationsActive size={18} />,
    PushNotification: <MdIcons.MdNotificationsActive size={18} />,
    Customer: <MdIcons.MdPerson size={18} />,
    Users: <MdIcons.MdPeople size={18} />,
    Roles: <MdIcons.MdAdminPanelSettings size={18} />,
    Permissions: <MdIcons.MdGppGood size={18} />,
    Sellers: <MdIcons.MdStorefront size={18} />,
    Supplier: <MdIcons.MdStore size={18} />,
    "Seller Payout": <MdIcons.MdAttachMoney size={18} />,
    "Supplier Payout": <MdIcons.MdAccountBalanceWallet size={18} />,
    ActivityLog: <MdIcons.MdTimeline size={18} />,
    LoginLogs: <MdIcons.MdSecurity size={18} />,
    Activity: <MdIcons.MdHistory size={18} />,
    MenuConfiguration: <MdIcons.MdMenu size={18} />,
    "Shipping & Payment Config": <MdIcons.MdSettingsApplications size={18} />,
    MobileContent: <MdIcons.MdPhoneIphone size={18} />,
    AppPolicy: <MdIcons.MdPolicy size={18} />,
    FAQs: <MdIcons.MdHelp size={18} />,
    Profile: <MdIcons.MdAccountBox size={18} />,
    "Sales & Revenue Report": <MdIcons.MdTrendingUp size={18} />,
    "Order Report": <MdIcons.MdReceipt size={18} />,
    "Invoice Report": <MdIcons.MdReceiptLong size={18} />,
    "Payment Report": <MdIcons.MdCreditCard size={18} />,
    "Shipping Report": <MdIcons.MdLocalShipping size={18} />,
    "Return & Refund Report": <MdIcons.MdAssignmentReturn size={18} />,
    "Product Sales Report": <MdIcons.MdBarChart size={18} />,
    "Category Report": <MdIcons.MdPieChart size={18} />,
    "Brand Report": <MdIcons.MdStarBorder size={18} />,
    "Inventory Report": <MdIcons.MdInventory size={18} />,
    "Customer Ratings Report": <MdIcons.MdStarRate size={18} />,
    "Profit & Loss Report": <MdIcons.MdBalance size={18} />,
    "GST Summary Report": <MdIcons.MdAccountBalance size={18} />,
    "Seller Sales & Commission Report": <MdIcons.MdBusinessCenter size={18} />,
    "Seller Settlement Report": <MdIcons.MdPayments size={18} />,
    "Supplier Purchase Report": <MdIcons.MdShoppingCartCheckout size={18} />,
    "Supplier Ledger Report": <MdIcons.MdMenuBook size={18} />,
    "Customer Report": <MdIcons.MdGroups size={18} />,
    "Campaign Report": <MdIcons.MdInsights size={18} />,
    "Marketing Report": <MdIcons.MdAdsClick size={18} />,
    "Support Report": <MdIcons.MdHeadsetMic size={18} />,
    "Warranty Report": <MdIcons.MdVerifiedUser size={18} />,
    Payments: <MdIcons.MdSavings size={18} />,
    Returns: <MdIcons.MdKeyboardReturn size={18} />,
    "Combo Offer": <MdIcons.MdCardGiftcard size={18} />,
    BOGO: <MdIcons.MdLoyalty size={18} />,
    Queries: <MdIcons.MdSupportAgent size={18} />,
  };

  // Get route map using the utility function
  const routeMap = getRouteMap(userType);

  // Get menu from login response (sessionStorage)
  useEffect(() => {
    const userData = sessionStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user.menus && Array.isArray(user.menus)) {
          setMenuItems(user.menus);
        } else {
          setMenuItems([]);
        }
      } catch {
        setMenuItems([]);
      }
    } else {
      setMenuItems([]);
    }
  }, []);



  // Helper to map login response menu to sidebar structure
  const mapMenu = (items) => {
    if (!Array.isArray(items)) return [];
    return items.map((item) => {
      let menuName = item.menuName || item.submenuName || item.name;

      // Rename Queries to Support Ticket for display and mapping
      if (menuName === "Queries") menuName = "Support Ticket";

      const route = routeMap[menuName];
      const submenus = item.submenus ? mapMenu(item.submenus) : [];

      // Filter out items that have no route and no submenus (except HR Management)
      if (!route && submenus.length === 0 && menuName !== "HR Management") {
        return null;
      }

      const isSubmenu = !!item.parentId || !!item.submenuId;

      // Always use frontend iconMap, ignore backend icons for Admin Panel
      let IconComponent = null;
      if (iconMap[menuName]) {
        IconComponent = iconMap[menuName];
      } else {
        // Distinct default icon for submenus to avoid repetition
        IconComponent = isSubmenu
          ? <MdFiberManualRecord size={10} style={{ marginLeft: '4px' }} />
          : <MdDashboard size={20} />;
      }

      return {
        key: item.menuId || item.submenuId || item._id,
        title: menuName,
        icon: IconComponent,
        to: route || '#',
        submenus: submenus,
      };
    }).filter(Boolean);
  };

  // Menu order and grouping live here (the database has no order field), so every role
  // gets the same tidy layout regardless of the order menus were added in.
  const rank = (name) => {
    const i = MENU_ORDER.indexOf(name);
    return i === -1 ? MENU_ORDER.length : i;
  };
  const sortMenu = (items) =>
    items
      .map((it, idx) => ({ it, idx }))
      .sort((a, b) => rank(a.it.title) - rank(b.it.title) || a.idx - b.idx)
      .map(({ it }) => ({ ...it, submenus: sortMenu(it.submenus || []) }));

  const mappedMenu = sortMenu(mapMenu(menuItems));

  const isRouteActive = (to) => !!to && to !== '#' && (location.pathname === to || location.pathname.startsWith(`${to}/`));
  const activeParentKey = (mappedMenu.find((s) => s.submenus.some((c) => isRouteActive(c.to))) || {}).key || null;

  useEffect(() => {
    if (activeParentKey) setOpenKey(activeParentKey);
  }, [activeParentKey]);

  // Top-level sections with small captions.
  const sections = (() => {
    const used = new Set();
    const out = TOP_GROUPS.map((g) => {
      const items = mappedMenu.filter((m) => g.names.includes(m.title));
      items.forEach((m) => used.add(m.key));
      return { label: g.label, items };
    }).filter((g) => g.items.length);
    const rest = mappedMenu.filter((m) => !used.has(m.key));
    if (rest.length) out.push({ label: 'More', items: rest });
    return out;
  })();

  // Children grouped under captions (Reports has 22 entries, so it's split into themes).
  const groupChildren = (parent) => {
    const defs = SUBGROUPS[parent.title];
    if (!defs) return [{ label: null, items: parent.submenus }];
    const used = new Set();
    const groups = defs
      .map((d) => {
        const items = d.items.map((n) => parent.submenus.find((c) => c.title === n)).filter(Boolean);
        items.forEach((c) => used.add(c.key));
        return { label: d.label, items };
      })
      .filter((g) => g.items.length);
    const rest = parent.submenus.filter((c) => !used.has(c.key));
    if (rest.length) groups.push({ label: 'Other', items: rest });
    return groups;
  };

  const handleItemClick = () => {
    if (onClose) onClose();
  };

  const renderChild = (item, stripReport) => {
    const label = DISPLAY_NAMES[item.title] || (stripReport ? item.title.replace(/ Report$/, '') : item.title);
    const body = (
      <ListItemButton sx={childButtonSx}>
        <ListItemIcon sx={{ minWidth: 30, color: 'inherit' }}>{item.icon}</ListItemIcon>
        <ListItemText primary={label} primaryTypographyProps={{ fontSize: 11.5, fontWeight: 500, lineHeight: 1.3 }} />
      </ListItemButton>
    );
    return (
      <ListItem key={item.key} disablePadding title={item.title}>
        {item.title === 'HR Management' ? (
          <Box onClick={() => setHrModalOpen(true)} sx={{ width: '100%' }}>{body}</Box>
        ) : (
          <StyledNavLink to={item.to} onClick={handleItemClick}>{body}</StyledNavLink>
        )}
      </ListItem>
    );
  };

  const renderSection = (section) => {
    const hasChildren = section.submenus && section.submenus.length > 0;
    const label = DISPLAY_NAMES[section.title] || section.title;
    const open = openKey === section.key;
    const childActive = activeParentKey === section.key;

    if (hasChildren) {
      const stripReport = section.title === 'Reports';
      return (
        <React.Fragment key={section.key}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setOpenKey(open ? null : section.key)}
              sx={{ ...parentButtonSx, ...(childActive && !open ? activeParentSx : null), ...(open ? { backgroundColor: 'rgba(15,23,42,0.05)' } : null) }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{section.icon}</ListItemIcon>
              <ListItemText primary={label} primaryTypographyProps={{ fontSize: 12.5, fontWeight: childActive || open ? 700 : 600 }} />
              <ChevronIcon size={20} style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', color: '#64748b' }} />
            </ListItemButton>
          </ListItem>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ ml: 3.5, pl: 1, borderLeft: '2px solid #e5e7eb', mb: 0.75 }}>
              {groupChildren(section).map((g) => (
                <React.Fragment key={g.label || 'all'}>
                  {g.label && <Typography sx={captionSx}>{g.label}</Typography>}
                  <List disablePadding>{g.items.map((c) => renderChild(c, stripReport))}</List>
                </React.Fragment>
              ))}
            </Box>
          </Collapse>
        </React.Fragment>
      );
    }

    const inner = (
      <ListItemButton sx={parentButtonSx}>
        <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{section.icon}</ListItemIcon>
        <ListItemText primary={label} primaryTypographyProps={{ fontSize: 12.5, fontWeight: 600 }} />
      </ListItemButton>
    );
    return (
      <ListItem key={section.key} disablePadding>
        {section.title === 'HR Management' ? (
          <Box onClick={() => setHrModalOpen(true)} sx={{ width: '100%' }}>{inner}</Box>
        ) : (
          <StyledNavLink to={section.to} onClick={handleItemClick}>{inner}</StyledNavLink>
        )}
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <LogoContainer>
        <img src="/img/glam.png" alt="Company Logo" style={{ height: '44px', maxWidth: '100%', objectFit: 'contain' }} />
      </LogoContainer>
      <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5, pb: 2 }}>
        {sections.map((g) => (
          <Box key={g.label}>
            <Typography sx={{ ...captionSx, px: 2.5, pt: 1.75, pb: 0.5 }}>{g.label}</Typography>
            <List disablePadding>{g.items.map(renderSection)}</List>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const paperSx = { '& .MuiDrawer-paper': { width: 290, boxSizing: 'border-box', borderRight: '1px solid #e5e7eb' } };

  return (
    <>
      {/* Mobile drawer */}
      <Drawer variant="temporary" open={isOpen} onClose={onClose} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', md: 'none' }, ...paperSx }}>
        {drawerContent}
      </Drawer>
      {/* Permanent sidebar for desktop */}
      <Drawer variant="permanent" open data-tour="sidebar" sx={{ display: { xs: 'none', md: 'block' }, ...paperSx }}>
        {drawerContent}
      </Drawer>

      {/* HR Management Redirect Modal */}
      <Dialog
        open={hrModalOpen}
        onClose={() => setHrModalOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "8px",
            minWidth: "350px"
          }
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700 }}>
          HR Management System
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ fontFamily: 'Mulish, sans-serif', mb: 2 }}>
            You are about to be redirected to the HR Management web application.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setHrModalOpen(false)}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontFamily: 'Mulish, sans-serif'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              window.open("https://hrms.glamcompany.in/login", "_blank");
              setHrModalOpen(false);
            }}
            sx={{
              backgroundColor: "rgb(255, 209, 2)",
              color: "black",
              fontWeight: 600,
              textTransform: 'none',
              fontFamily: 'Mulish, sans-serif',
              '&:hover': {
                backgroundColor: "rgb(230, 189, 0)",
              }
            }}
          >
            Go to HRMS
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Sidebar;